import { Injectable } from '@angular/core';
import { NormalizedLandmark } from '@mediapipe/tasks-vision';
import {
	DEFAULT_FACE_DEPTH_REAL_THRESHOLDS,
	FaceDepthRealScores,
	FaceDepthRealThresholds,
	FaceDepthRoiSnapshot,
} from './face-depth-real.models';
import {
	Face3dLabLandmark,
	FaceRoiTextureMetrics,
	computeSimilarityResidual,
	extractFaceGeometrySnapshot,
	normalizedCrossCorrelation,
	scoreFromRatio,
} from './face-3d-lab-geometry.util';

/** Escala heurística: meanDepth_est ≈ REF / bboxScale (pseudo-metros para web). */
const WEB_DEPTH_REF = 0.45;

@Injectable({
	providedIn: 'root',
})
export class FaceDepthRealLabService {
	public getDefaultThresholds(): FaceDepthRealThresholds {
		return { ...DEFAULT_FACE_DEPTH_REAL_THRESHOLDS };
	}

	/**
	 * Protocolo (min≤delta≤max) + 2 métricas anti-foto:
	 * A = forma (residual de homotecia far→near); B = diferencia de imagen (1−NCC) + veto.
	 * Veto plano: residual < maxFlatShapeResidual → FAIL (sin bypass de apariencia).
	 * Bypass de forma (score) solo si apariencia fuerte y residual no es plano.
	 * Laplaciano / zDecoupling: solo diagnóstico.
	 */
	public scoreApproach(
		far: FaceDepthRoiSnapshot,
		near: FaceDepthRoiSnapshot,
		thresholds: FaceDepthRealThresholds = DEFAULT_FACE_DEPTH_REAL_THRESHOLDS,
	): FaceDepthRealScores {
		const deltaDepthM = Math.max(0, far.meanDepthM - near.meanDepthM);
		const scaleProxy = far.meanDepthM / Math.max(0.05, near.meanDepthM);

		const farPlanar = Math.max(1e-6, far.planarFitErrorM);
		const nearPlanar = Math.max(1e-6, near.planarFitErrorM);
		const farRel = Math.sqrt(Math.max(0, far.varianceM2));
		const nearRel = Math.sqrt(Math.max(0, near.varianceM2));
		const planarGrowth = nearPlanar / farPlanar;
		const varGrowth = nearRel / Math.max(1e-6, farRel);
		const zDecoupling =
			(Math.abs(planarGrowth - scaleProxy) + Math.abs(varGrowth - scaleProxy)) /
			Math.max(0.5, scaleProxy);

		const detailDelta = this.computeDetailDelta(far.texture, near.texture);

		const farOff = far.keyOffsets ?? [];
		const nearOff = near.keyOffsets ?? [];
		const hasShape = farOff.length >= 3 && nearOff.length >= 3;
		const shapeResidual = hasShape ? computeSimilarityResidual(farOff, nearOff) : 0;
		const shapeScore = hasShape
			? scoreFromRatio(
					shapeResidual,
					thresholds.fullShapeResidual * 0.22,
					thresholds.fullShapeResidual,
				)
			: 0;

		const appearanceDelta = this.computeAppearanceDelta(far.texture, near.texture);
		const appearanceScore = scoreFromRatio(
			appearanceDelta,
			thresholds.fullAppearanceDelta * 0.28,
			thresholds.fullAppearanceDelta,
		);

		const weightSum = Math.max(0.001, thresholds.weightShape + thresholds.weightAppearance);
		const finalScore =
			(shapeScore * thresholds.weightShape + appearanceScore * thresholds.weightAppearance) /
			weightSum;

		const hasTexture = !!(far.texture?.patch16?.length && near.texture?.patch16?.length);
		const samplesOk =
			far.validSampleRatio >= thresholds.minValidSampleRatio &&
			near.validSampleRatio >= thresholds.minValidSampleRatio;
		const maxDelta = Math.max(thresholds.minDeltaDepthM, thresholds.maxDeltaDepthM);
		const gateDeltaOk =
			samplesOk &&
			deltaDepthM >= thresholds.minDeltaDepthM &&
			deltaDepthM <= maxDelta;
		/** Firma foto plana (approach): residual muy bajo no se salva con apariencia. */
		const gateFlatShapeOk =
			samplesOk && hasShape && shapeResidual >= thresholds.maxFlatShapeResidual;
		// Bypass de score de forma: apariencia fuerte, pero solo si no es residual plano.
		const strongAppearance =
			appearanceDelta >= Math.max(thresholds.minAppearanceDelta + 0.07, 0.22) &&
			appearanceScore >= 80;
		const gateShapeOk =
			samplesOk &&
			gateFlatShapeOk &&
			((hasShape && shapeScore >= thresholds.minShapeScore) ||
				(hasTexture && strongAppearance));
		const gateAppearanceOk =
			samplesOk &&
			hasTexture &&
			appearanceDelta >= thresholds.minAppearanceDelta &&
			appearanceScore >= thresholds.minAppearanceScore;
		const passed =
			gateDeltaOk &&
			gateFlatShapeOk &&
			gateShapeOk &&
			gateAppearanceOk &&
			finalScore >= thresholds.minFinalScore;

		return {
			meanDepthFarM: far.meanDepthM,
			meanDepthNearM: near.meanDepthM,
			deltaDepthM,
			zDecoupling,
			detailDelta,
			shapeResidual,
			shapeScore,
			appearanceDelta,
			appearanceScore,
			finalScore,
			gateDeltaOk,
			gateShapeOk,
			gateAppearanceOk,
			gateFlatShapeOk,
			passed,
		};
	}

	/**
	 * Profundidad estimada web: distancia proxy por tamaño de cara + relieve Z MediaPipe.
	 * `analysisZoom` corrige el crop ampliado (fase lejos).
	 */
	public buildRoiSnapshotFromLandmarks(
		landmarks: NormalizedLandmark[],
		capturedAtMs: number = performance.now(),
		analysisZoom: number = 1,
		texture: FaceRoiTextureMetrics | null = null,
	): FaceDepthRoiSnapshot | null {
		const geo = extractFaceGeometrySnapshot(landmarks);
		if (!geo) {
			return null;
		}

		const zoom = Math.max(1, analysisZoom);
		const physicalBboxScale = geo.bboxScale / zoom;
		const meanDepthM = WEB_DEPTH_REF / Math.max(0.05, physicalBboxScale);
		const keyIdx = [
			Face3dLabLandmark.NoseTip,
			Face3dLabLandmark.Forehead,
			Face3dLabLandmark.Chin,
			Face3dLabLandmark.LeftEar,
			Face3dLabLandmark.RightEar,
			Face3dLabLandmark.LeftCheek,
			Face3dLabLandmark.RightCheek,
		];
		const samples: { x: number; y: number; z: number }[] = [];
		for (const i of keyIdx) {
			const p = landmarks[i];
			if (!p) {
				continue;
			}
			samples.push({ x: p.x, y: p.y, z: typeof p.z === 'number' ? p.z : 0 });
		}
		if (samples.length < 3) {
			return null;
		}

		const zMean = samples.reduce((a, s) => a + s.z, 0) / samples.length;
		const varianceM2 = samples.reduce((a, s) => a + (s.z - zMean) ** 2, 0) / samples.length;
		const planarFitErrorM = this.planarRmseMeters(samples);
		const keyOffsets = geo.keyOffsets.map((o) => ({ dx: o.dx / zoom, dy: o.dy / zoom }));

		return {
			meanDepthM,
			varianceM2,
			validSampleRatio: 1,
			planarFitErrorM,
			capturedAtMs,
			texture,
			keyOffsets,
		};
	}

	/**
	 * Agrega un depth map Float32 (metros) en ROI normalizado [0–1].
	 * No retiene el buffer de entrada.
	 */
	public buildRoiSnapshotFromDepthMeters(
		depthMeters: Float32Array,
		width: number,
		height: number,
		roi: { x0: number; y0: number; x1: number; y1: number } = { x0: 0.3, y0: 0.25, x1: 0.7, y1: 0.75 },
		capturedAtMs: number = performance.now(),
		texture: FaceRoiTextureMetrics | null = null,
	): FaceDepthRoiSnapshot {
		const col0 = Math.max(0, Math.floor(roi.x0 * width));
		const col1 = Math.min(width, Math.ceil(roi.x1 * width));
		const row0 = Math.max(0, Math.floor(roi.y0 * height));
		const row1 = Math.min(height, Math.ceil(roi.y1 * height));

		const samples: { x: number; y: number; z: number }[] = [];
		const step = Math.max(1, Math.floor(Math.min(width, height) / 40));
		let considered = 0;

		for (let y = row0; y < row1; y += step) {
			for (let x = col0; x < col1; x += step) {
				considered++;
				const z = depthMeters[y * width + x];
				if (Number.isFinite(z) && z > 0.05 && z < 4) {
					samples.push({ x, y, z });
				}
			}
		}

		if (samples.length < 3) {
			return {
				meanDepthM: 0,
				varianceM2: 0,
				validSampleRatio: considered > 0 ? samples.length / considered : 0,
				planarFitErrorM: 0,
				capturedAtMs,
				texture,
			};
		}

		const mean = samples.reduce((acc, s) => acc + s.z, 0) / samples.length;
		const variance = samples.reduce((acc, s) => acc + (s.z - mean) ** 2, 0) / samples.length;
		const planarFitErrorM = this.planarRmseMeters(samples);

		return {
			meanDepthM: mean,
			varianceM2: variance,
			validSampleRatio: samples.length / considered,
			planarFitErrorM,
			capturedAtMs,
			texture,
		};
	}

	private computeAppearanceDelta(
		farTex: FaceRoiTextureMetrics | null | undefined,
		nearTex: FaceRoiTextureMetrics | null | undefined,
	): number {
		if (!farTex?.patch16?.length || !nearTex?.patch16?.length) {
			return 0;
		}
		const ncc = normalizedCrossCorrelation(farTex.patch16, nearTex.patch16);
		return clamp01Local(1 - ncc);
	}

	/** Cambio relativo de “detalle” (varianza laplaciana) entre far y near. */
	private computeDetailDelta(
		farTex: FaceRoiTextureMetrics | null | undefined,
		nearTex: FaceRoiTextureMetrics | null | undefined,
	): number {
		if (!farTex || !nearTex) {
			return 0;
		}
		const a = Math.max(0, farTex.laplacianVar);
		const b = Math.max(0, nearTex.laplacianVar);
		const denom = Math.max(a, b, 1e-3);
		return clamp01Local(Math.abs(b - a) / denom);
	}

	/** Ajuste plano z ≈ ax + by + c por mínimos cuadrados; RMSE en metros. */
	private planarRmseMeters(samples: { x: number; y: number; z: number }[]): number {
		let sumX = 0;
		let sumY = 0;
		let sumZ = 0;
		let sumXX = 0;
		let sumYY = 0;
		let sumXY = 0;
		let sumXZ = 0;
		let sumYZ = 0;
		const n = samples.length;
		for (const s of samples) {
			sumX += s.x;
			sumY += s.y;
			sumZ += s.z;
			sumXX += s.x * s.x;
			sumYY += s.y * s.y;
			sumXY += s.x * s.y;
			sumXZ += s.x * s.z;
			sumYZ += s.y * s.z;
		}

		const det =
			sumXX * (sumYY * n - sumY * sumY) -
			sumXY * (sumXY * n - sumY * sumX) +
			sumX * (sumXY * sumY - sumYY * sumX);
		if (Math.abs(det) < 1e-9) {
			return Math.sqrt(Math.max(0, samples.reduce((a, s) => a + (s.z - sumZ / n) ** 2, 0) / n));
		}

		const detA =
			sumXZ * (sumYY * n - sumY * sumY) -
			sumXY * (sumYZ * n - sumY * sumZ) +
			sumX * (sumYZ * sumY - sumYY * sumZ);
		const detB =
			sumXX * (sumYZ * n - sumY * sumZ) -
			sumXZ * (sumXY * n - sumY * sumX) +
			sumX * (sumXY * sumZ - sumYZ * sumX);
		const detC =
			sumXX * (sumYY * sumZ - sumY * sumYZ) -
			sumXY * (sumXY * sumZ - sumY * sumXZ) +
			sumXZ * (sumXY * sumY - sumYY * sumX);

		const a = detA / det;
		const b = detB / det;
		const c = detC / det;
		let err = 0;
		for (const s of samples) {
			const pred = a * s.x + b * s.y + c;
			err += (s.z - pred) ** 2;
		}
		return Math.sqrt(err / n);
	}
}

function clamp01Local(value: number): number {
	if (!Number.isFinite(value)) {
		return 0;
	}
	return Math.min(1, Math.max(0, value));
}
